import React from 'react';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { Input } from '@/components/ui/atoms/Input';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';

import {
  DEFAULT_MATCHING_PRIORITIES,
  MatchingPriorities,
  Skill,
} from '../../../models/job';
import { getJobFieldLabel, getTranslatedSelectOptions } from '../../../utils/job-field-i18n';
import { BLOCKING_FIELDS } from '../../../utils/missingFieldsDetection';
import { MatchingPrioritySelector } from '../matching-weights/MatchingPrioritySelector';
import { DraggableSkillClassifier } from './DraggableSkillClassifier';
import { isNumberField, isSelectField, isTextareaField } from './fieldConfig';

type DashboardKey = ParseKeys<'dashboard'>;

type SectionDef = { key: string; label: DashboardKey; hint: DashboardKey };

/** Shared create/refine + edit form sections (pill progress). */
export const REFINE_SECTIONS = [
  { key: 'details', label: 'create_job_modal.section_job_details', hint: 'create_job_modal.hint_job_details' },
  { key: 'skills', label: 'create_job_modal.section_skills', hint: 'create_job_modal.hint_skills' },
  { key: 'company', label: 'create_job_modal.section_company', hint: 'create_job_modal.hint_company' },
  { key: 'weighting', label: 'create_job_modal.section_weighting', hint: 'create_job_modal.hint_weighting' },
] as const satisfies ReadonlyArray<SectionDef>;

/** Edit-only Review chrome (rescore impact). Not part of create/refine pills. */
export const REVIEW_SECTION = {
  key: 'review',
  label: 'create_job_modal.section_review',
  hint: 'create_job_modal.hint_review',
} as const satisfies SectionDef;

export type RefineSectionKey = (typeof REFINE_SECTIONS)[number]['key'];

export type JobRefineFieldValue = string | Skill[] | MatchingPriorities | undefined;

export interface JobRefineFieldBag {
  getValue: (fieldName: string) => JobRefineFieldValue;
  onChange: (fieldName: string, value: string | Skill[] | MatchingPriorities) => void;
  /** Fallback when Matching Priorities is unset (create uses job; edit uses form default). */
  defaultMatchingPriorities?: MatchingPriorities;
}

const isFieldRequired = (fieldName: string) =>
  (BLOCKING_FIELDS as readonly string[]).includes(fieldName);

const isEmptyValue = (value: JobRefineFieldValue): boolean => {
  if (value === undefined || value === null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return false;
  return String(value).trim() === '';
};

interface JobRefineSectionHeaderProps {
  sections: ReadonlyArray<SectionDef>;
  sectionIdx: number;
  /** `pills` for in-phase section progress; `none` when outer ModalStepper is enough (e.g. Review). */
  progress?: 'pills' | 'none';
}

/** Section title + hint; optional pill dots (create/refine + edit Details phase). */
export const JobRefineSectionHeader: React.FC<JobRefineSectionHeaderProps> = ({
  sections,
  sectionIdx,
  progress = 'pills',
}) => {
  const { t } = useTranslation('dashboard');
  const section = sections[Math.min(sectionIdx, sections.length - 1)];
  const showPills = progress === 'pills';

  return (
    <div className={`flex items-center mb-5 ${showPills ? 'justify-between' : ''}`}>
      <div>
        <h6 className="font-heading text-dark-invirtus flex items-center gap-1.5">
          {t(section.label)}
          {section.key === 'skills' && (
            <Tooltip content={t('create_job_modal.skills_tooltip')} variant="top">
              <Info className="h-3.5 w-3.5 text-gray-400 hover:text-purple cursor-help" />
            </Tooltip>
          )}
        </h6>
        <p className="text-xs text-gray-500 font-body mt-0.5">{t(section.hint)}</p>
      </div>
      {showPills && (
        <div
          className="flex items-center gap-1.5"
          aria-label={t('create_job_modal.section_of', {
            current: sectionIdx + 1,
            total: sections.length,
          })}
        >
          {sections.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === sectionIdx ? 'w-6 bg-purple' : 'w-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** Field layouts shared by create/refine ReviewState and EditJobDetailsModal. */
export const JobRefineSectionFields: React.FC<
  JobRefineFieldBag & { sectionKey: RefineSectionKey }
> = ({ sectionKey, getValue, onChange, defaultMatchingPriorities }) => {
  const { t } = useTranslation('dashboard');

  const labelFor = (name: string, required = false) => (
    <label className="block text-sm font-medium text-dark-invirtus font-heading">
      {getJobFieldLabel(name, t)}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  const renderFieldInput = (fieldName: string, opts?: { grow?: boolean }) => {
    const value = getValue(fieldName);
    const empty = isEmptyValue(value);
    const missingClass = empty ? 'border-purple bg-purple/[0.03]' : undefined;
    const placeholder = t('create_job_modal.enter_field', {
      field: getJobFieldLabel(fieldName, t).toLowerCase(),
    });

    if (fieldName === 'Skills') {
      const skills = (value as Skill[]) || [];
      return (
        <DraggableSkillClassifier
          skills={skills}
          onSkillsChange={(updated) => onChange(fieldName, updated)}
          type="framework"
        />
      );
    }

    if (isNumberField(fieldName) || fieldName === 'Years of Experience') {
      return (
        <Input
          type="number"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(fieldName, e.target.value)}
          placeholder={placeholder}
          rounded="md"
          min={0}
          className={missingClass}
        />
      );
    }

    if (isSelectField(fieldName)) {
      const options = getTranslatedSelectOptions(fieldName, t);
      return (
        <select
          value={(value as string) ?? ''}
          onChange={(e) => onChange(fieldName, e.target.value)}
          className={cn(
            'w-full px-4 py-2.5 border rounded-md text-dark-invirtus placeholder:italic placeholder-gray-400 focus:outline-none focus:ring-1 transition-colors bg-white font-body text-sm appearance-none cursor-pointer',
            empty
              ? 'border-purple bg-purple/[0.03] focus:ring-purple/30 focus:border-purple italic text-gray-400'
              : 'border-gray-200 focus:ring-purple/30 focus:border-purple'
          )}
        >
          <option value="" disabled>
            {t('create_job_modal.select_field', {
              field: getJobFieldLabel(fieldName, t).toLowerCase(),
            })}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} className="not-italic text-dark-invirtus">
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (isTextareaField(fieldName) || fieldName === 'Role Description') {
      return (
        <textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(fieldName, e.target.value)}
          rows={opts?.grow ? 4 : 3}
          placeholder={placeholder}
          className={cn(
            'w-full px-4 py-2.5 border rounded-md text-dark-invirtus placeholder:italic placeholder-gray-400 focus:outline-none focus:ring-1 transition-colors bg-white font-body text-sm resize-y',
            empty
              ? 'border-purple bg-purple/[0.03] focus:ring-purple/30 focus:border-purple'
              : 'border-gray-200 focus:ring-purple/30 focus:border-purple',
            // Cap height — do not flex-grow into empty modal space (inflates fields).
            opts?.grow && 'min-h-[6rem] max-h-[12rem]'
          )}
        />
      );
    }

    return (
      <Input
        type="text"
        value={(value as string) ?? ''}
        onChange={(e) => onChange(fieldName, e.target.value)}
        placeholder={placeholder}
        rounded="md"
        className={missingClass}
      />
    );
  };

  if (sectionKey === 'details') {
    return (
      <div className="flex flex-col gap-4">
        <div className="space-y-1.5">
          {labelFor('Job Title', true)}
          {renderFieldInput('Job Title')}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {(['Location', 'Contract Type', 'Remote Policy'] as const).map((f) => (
            <div key={f} className="space-y-1.5">
              {labelFor(f, isFieldRequired(f))}
              {renderFieldInput(f)}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {(['Years of Experience', 'Required Degree'] as const).map((f) => (
            <div key={f} className="space-y-1.5">
              {labelFor(f, isFieldRequired(f))}
              {renderFieldInput(f)}
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-1.5">
          {labelFor('Role Description', isFieldRequired('Role Description'))}
          {renderFieldInput('Role Description', { grow: true })}
        </div>
      </div>
    );
  }

  if (sectionKey === 'skills') {
    return <div className="h-full min-h-0">{renderFieldInput('Skills')}</div>;
  }

  if (sectionKey === 'company') {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {(['Company Name', 'Company Sector'] as const).map((f) => (
            <div key={f} className="space-y-1.5">
              {labelFor(f, isFieldRequired(f))}
              {renderFieldInput(f)}
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-1.5">
          {labelFor('Company Description', isFieldRequired('Company Description'))}
          {renderFieldInput('Company Description', { grow: true })}
        </div>
      </div>
    );
  }

  const priorities =
    (getValue('Matching Priorities') as MatchingPriorities | undefined) ??
    defaultMatchingPriorities ??
    DEFAULT_MATCHING_PRIORITIES;

  const labels = {
    yoe: t('create_job_modal.weighting_fields.yoe'),
    skills_match: t('create_job_modal.weighting_fields.skills_match'),
    degree: t('create_job_modal.weighting_fields.degree'),
    sector_experience: t('create_job_modal.weighting_fields.sector_experience'),
  } as const;

  const priorityLabels = {
    low: t('create_job_modal.priority_levels.low'),
    medium: t('create_job_modal.priority_levels.medium'),
    high: t('create_job_modal.priority_levels.high'),
    top: t('create_job_modal.priority_levels.top'),
  } as const;

  return (
    <div className="flex flex-col gap-4">
      <MatchingPrioritySelector
        value={priorities}
        labels={labels}
        priorityLabels={priorityLabels}
        onChange={(next) => onChange('Matching Priorities', next)}
      />
    </div>
  );
};
